import * as DocumentPicker from 'expo-document-picker'
import type * as FileSystem from 'expo-file-system/legacy'
import { Platform } from 'react-native'
import ReactNativeBlobUtil from 'react-native-blob-util'

import Snackbar from '@/components/snackbar/Snackbar'
import createUploadTasks, {
  UploadTaskFile,
} from '@/hooks/uploads/createUploadTasks'
import getUploadedFileId from '@/hooks/uploads/uploadResult'
import DocumentScanner, {
  ResponseType,
  ScanDocumentResponseStatus,
} from '@/services/documentScanner'
import { useAppStore } from '@/store/useAppStore'
import log from '@/utils/datadog/log/log'
import {
  downloadFileFromUrl,
  generateDocumentFilename,
  openFileInBrowser,
} from '@/utils/webDownload'
import { ApiRoot } from '../../constants'

const DOCUMENT_OPEN_CACHE_VERSION = 'v2'

const useFile = () => {
  const currentOrganization = useAppStore.use.currentOrganization().organization

  const accessToken = useAppStore.use.auth().accessToken
  const uploadUrl = `${ApiRoot()}/${currentOrganization?.id}/fileUpload`

  const scanFiles = async () => {
    const result = await DocumentScanner.scanDocument({
      croppedImageQuality: 100,
      responseType: ResponseType.ImageFilePath,
    })

    if (result.status === ScanDocumentResponseStatus.Success) {
      if (!result.scannedImages) {
        Snackbar.error(
          'There was an error scanning the documents. Please try again.'
        )
        return
      }

      return result.scannedImages
    }
  }

  const buildUploadTasks = ({
    files,
    progressCallback,
  }: {
    files: UploadTaskFile[]
    progressCallback?: (
      progress: FileSystem.UploadProgressData,
      fileIndex: number
    ) => void
  }) =>
    createUploadTasks({
      uploadUrl,
      accessToken: accessToken ?? '',
      files,
      progressCallback,
    })

  const pickFilesAndUpload = async ({
    allowedFileTypes,
    multiple,
  }: {
    allowedFileTypes: string[]
    multiple: boolean
  }) => {
    const pickResult = await DocumentPicker.getDocumentAsync({
      type: allowedFileTypes,
      multiple,
    })

    if (!pickResult.assets?.length) {
      return
    }

    const files = pickResult.assets.map((asset) => ({
      fileName: asset.name,
      fileUri: asset.uri,
    }))

    return uploadFiles(files)
  }

  const uploadFiles = async (files: UploadTaskFile[]) => {
    const tasks = buildUploadTasks({ files })
    return Promise.all(
      tasks.map(async (task) => getUploadedFileId(await task.uploadAsync()))
    )
  }

  const openFile = async ({
    fileName,
    fileId,
    mimeType,
  }: {
    fileName?: string | null
    fileId?: string | null
    mimeType?: string | null
    fosterName?: string
    documentType?: string
    contextName?: string
  }) => {
    const fileURL = `${ApiRoot()}/${currentOrganization?.id}/${fileId}/download`

    if (!fileURL || !mimeType) {
      log.error(
        'There was a problem opening the file. fileURL was undefined or null'
      )

      Snackbar.error('There was a problem opening the file.')
      return
    }

    if (Platform.OS === 'web') {
      try {
        await openFileInBrowser(fileURL, {
          Authorization: `Bearer ${accessToken}`,
        })
      } catch (error) {
        log.error('There was a problem opening the file.', { error })
        Snackbar.error('There was a problem opening the file.')
      }
    } else {
      const nativeFileURL = `${fileURL}?cacheVersion=${DOCUMENT_OPEN_CACHE_VERSION}`
      const filePath =
        ReactNativeBlobUtil.fs.dirs.CacheDir +
        `/${fileId}/${DOCUMENT_OPEN_CACHE_VERSION}-${fileName}`

      if (!(await ReactNativeBlobUtil.fs.exists(filePath))) {
        const downloadResponse = await ReactNativeBlobUtil.fetch(
          'GET',
          nativeFileURL,
          {
            Authorization: `Bearer ${accessToken}`,
          }
        )

        await ReactNativeBlobUtil.fs.writeFile(
          filePath,
          downloadResponse.data,
          'base64'
        )
      }

      if (Platform.OS === 'android') {
        await ReactNativeBlobUtil.android.actionViewIntent(filePath, mimeType)
      } else if (Platform.OS === 'ios') {
        // We try twice, because sometimes the first time we open the file, it doesn't work.
        // This issue might be related https://github.com/RonRadtke/react-native-blob-util/issues/267
        // We're tracking the error in Datadog https://app.datadoghq.com/error-tracking?query=&refresh_mode=sliding&source=all&sp=%5B%7B%22p%22%3A%7B%22issueId%22%3A%228d83e2d8-dc29-11ef-b340-da7ad0900002%22%7D%2C%22i%22%3A%22error-tracking-issue%22%7D%5D&from_ts=1744642918239&to_ts=1744646518239&live=true
        // So far as of this commit it's only Stevie that's been seeing this issue.
        try {
          ReactNativeBlobUtil.ios.openDocument(filePath)
        } catch (error) {
          log.error('There was a problem opening the file.', { error })
          ReactNativeBlobUtil.ios.openDocument(filePath)
        }
      }
    }
  }

  const downloadFile = async ({
    fileName,
    fileId,
    mimeType,
    fosterName,
    documentType,
    contextName,
  }: {
    fileName?: string | null
    fileId?: string | null
    mimeType?: string | null
    fosterName?: string
    documentType?: string
    contextName?: string
  }) => {
    if (Platform.OS !== 'web') {
      return
    }

    const fileURL = `${ApiRoot()}/${currentOrganization?.id}/${fileId}/download`

    if (!fileURL || !mimeType) {
      Snackbar.error('There was a problem downloading the file.')
      return
    }

    try {
      const downloadFilename = generateDocumentFilename(
        fileName,
        mimeType,
        documentType,
        fosterName,
        contextName
      )

      await downloadFileFromUrl(`${fileURL}?download=true`, downloadFilename, {
        Authorization: `Bearer ${accessToken}`,
      })
    } catch (error) {
      log.error('There was a problem downloading the file.', { error })
      Snackbar.error('There was a problem downloading the file.')
    }
  }

  return {
    openFile,
    downloadFile,
    pickFilesAndUpload,
    createUploadTasks: buildUploadTasks,
    scanFiles,
    uploadFiles,
  }
}

export default useFile
